import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tag, Plus, Trash, Sparkle, PencilSimple, Percent, CurrencyInr } from "@phosphor-icons/react";
import { PageLoader, EmptyState, StatusBadge } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import api, { money, apiErr } from "../../lib/api";

const blankCoupon = () => ({
  code: "", discount_type: "percent", value: 10, min_amount: 0, max_discount: "",
  usage_limit: "", per_user_limit: "", starts_at: "", expires_at: "", active: true,
});

export default function Coupons() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blankCoupon());
  const [aiCmd, setAiCmd] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/organizer/events").then((r) => {
      setEvents(r.data.events);
      if (r.data.events[0]) setEventId(r.data.events[0].id);
    }).finally(() => setLoading(false));
  }, []);

  const load = () => {
    if (!eventId) return;
    setListLoading(true);
    api.get(`/organizer/events/${eventId}/coupons`).then((r) => setCoupons(r.data.coupons)).finally(() => setListLoading(false));
  };
  useEffect(() => { load(); }, [eventId]); // eslint-disable-line

  const openNew = () => { setEditId(null); setForm(blankCoupon()); setAiCmd(""); setOpen(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      code: c.code, discount_type: c.discount_type, value: c.value, min_amount: c.min_amount || 0,
      max_discount: c.max_discount ?? "", usage_limit: c.usage_limit ?? "", per_user_limit: c.per_user_limit ?? "",
      starts_at: (c.starts_at || "").slice(0, 16), expires_at: (c.expires_at || "").slice(0, 16), active: c.active,
    });
    setOpen(true);
  };

  const aiGenerate = async () => {
    if (!aiCmd.trim()) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/organizer/coupons/ai", { event_id: eventId, command: aiCmd });
      const d = data.draft || {};
      setForm((f) => ({
        ...f, code: d.code || f.code, discount_type: d.discount_type || "percent", value: d.value ?? f.value,
        min_amount: d.min_amount ?? 0, max_discount: d.max_discount ?? "", usage_limit: d.usage_limit ?? "",
        per_user_limit: d.per_user_limit ?? "", starts_at: (d.starts_at || "").slice(0, 16), expires_at: (d.expires_at || "").slice(0, 16),
        active: d.active !== false,
      }));
      toast.success("AI drafted a coupon — review and save.");
    } catch (e) { toast.error(apiErr(e)); } finally { setAiBusy(false); }
  };

  const payload = () => ({
    code: form.code.trim().toUpperCase(),
    discount_type: form.discount_type,
    value: Number(form.value) || 0,
    min_amount: Number(form.min_amount) || 0,
    max_discount: form.max_discount === "" ? null : Number(form.max_discount),
    usage_limit: form.usage_limit === "" ? null : parseInt(form.usage_limit, 10),
    per_user_limit: form.per_user_limit === "" ? null : parseInt(form.per_user_limit, 10),
    starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
    expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
    active: form.active,
  });

  const save = async () => {
    if (!form.code.trim()) { toast.error("Enter a coupon code."); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/organizer/coupons/${editId}`, payload());
      else await api.post(`/organizer/events/${eventId}/coupons`, payload());
      toast.success(editId ? "Coupon updated." : "Coupon created.");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    try { await api.put(`/organizer/coupons/${c.id}`, { active: !c.active }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (c) => {
    try { await api.delete(`/organizer/coupons/${c.id}`); toast.success("Coupon deleted."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="max-w-4xl">
      <div className="overline mb-1">Promotions</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-6">Event Coupons</h1>

      {events.length === 0 ? (
        <EmptyState icon={Tag} title="No events yet" subtitle="Create an event first to add coupons."
          action={<Button asChild className="rounded-full"><Link to="/organizer/events/new">Create event</Link></Button>} testid="no-events-coupons" />
      ) : (
        <>
          <div className="flex flex-wrap gap-3 items-end justify-between mb-5">
            <div className="w-full sm:w-72">
              <Label className="mb-1.5 block">Event</Label>
              <Select value={eventId} onValueChange={setEventId}>
                <SelectTrigger data-testid="coupon-event-select"><SelectValue placeholder="Select event" /></SelectTrigger>
                <SelectContent>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button className="rounded-full" onClick={openNew} data-testid="new-coupon-btn"><Plus className="mr-1.5" /> New Coupon</Button>
          </div>

          {listLoading ? <PageLoader /> : coupons.length === 0 ? (
            <EmptyState icon={Tag} title="No coupons for this event" subtitle="Create a discount code, or describe one to AI." testid="no-coupons" />
          ) : (
            <div className="space-y-3">
              {coupons.map((c) => (
                <div key={c.id} className="p-4 rounded-xl border border-border bg-card" data-testid={`coupon-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-lg tracking-wider">{c.code}</span>
                        <StatusBadge status={c.status} />
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {c.discount_type === "percent" ? `${c.value}% off` : `${money(c.value)} off`}
                        {c.max_discount ? ` (max ${money(c.max_discount)})` : ""}
                        {c.min_amount ? ` · min ${money(c.min_amount)}` : ""}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {c.redemptions} used{c.usage_limit ? ` / ${c.usage_limit}` : ""} · {money(c.discount_given)} given
                        {c.expires_at ? ` · expires ${new Date(c.expires_at).toLocaleDateString("en-IN")}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Switch checked={c.active} onCheckedChange={() => toggleActive(c)} data-testid={`coupon-active-${c.id}`} />
                      <button onClick={() => openEdit(c)} className="text-muted-foreground hover:text-foreground" data-testid={`edit-coupon-${c.id}`}><PencilSimple size={16} /></button>
                      <button onClick={() => remove(c)} className="text-destructive" data-testid={`del-coupon-${c.id}`}><Trash size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="coupon-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit coupon" : "New coupon"}</DialogTitle></DialogHeader>

          {!editId && (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
              <Label className="flex items-center gap-1.5 mb-2 text-primary"><Sparkle weight="fill" size={15} /> Create with AI</Label>
              <div className="flex gap-2">
                <Textarea value={aiCmd} onChange={(e) => setAiCmd(e.target.value)} rows={1} placeholder='e.g. "20% off, max ₹300, first 50 bookings, ends Friday midnight"' data-testid="coupon-ai-cmd" />
                <Button onClick={aiGenerate} disabled={aiBusy} className="rounded-full shrink-0" data-testid="coupon-ai-generate">{aiBusy ? "…" : "Draft"}</Button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="mt-1.5 font-mono" data-testid="coupon-code" placeholder="SAVE20" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="coupon-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent"><span className="inline-flex items-center gap-1"><Percent size={13} /> Percent</span></SelectItem>
                    <SelectItem value="flat"><span className="inline-flex items-center gap-1"><CurrencyInr size={13} /> Flat</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{form.discount_type === "percent" ? "Percent (%)" : "Amount (₹)"}</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1.5" data-testid="coupon-value" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Min order (₹)</Label><Input type="number" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} className="mt-1.5" data-testid="coupon-min" /></div>
              <div><Label>Max discount (₹)</Label><Input type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} className="mt-1.5" placeholder="none" data-testid="coupon-max" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Total uses</Label><Input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} className="mt-1.5" placeholder="unlimited" data-testid="coupon-usage" /></div>
              <div><Label>Per-user uses</Label><Input type="number" value={form.per_user_limit} onChange={(e) => setForm({ ...form, per_user_limit: e.target.value })} className="mt-1.5" placeholder="unlimited" data-testid="coupon-per-user" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Starts</Label><Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="mt-1.5" data-testid="coupon-starts" /></div>
              <div><Label>Expires</Label><Input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1.5" data-testid="coupon-expires" /></div>
            </div>
            <label className="flex items-center justify-between p-3 rounded-lg border border-border">
              <span className="text-sm">Active</span>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="coupon-active-toggle" />
            </label>
          </div>
          <DialogFooter><Button onClick={save} disabled={saving} className="w-full rounded-full" data-testid="save-coupon">{saving ? "Saving…" : editId ? "Update coupon" : "Create coupon"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
